import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import InfoIcon from '@mui/icons-material/Info'
import HistoryIcon from '@mui/icons-material/History'
import ErrorIcon from '@mui/icons-material/Error'
import { inventoryService } from '../services'
import { formatDateTime } from '../utils/dateFormat'
import { getUploadSuccessMessage } from '../utils/uploadSuccessMessages'
import UploadErrorDetailsDialog from '../components/UploadErrorDetailsDialog'
import UploadLogStatusChip from '../components/UploadLogStatusChip'
import UploadHistoryRefreshButton from '../components/UploadHistoryRefreshButton'
import UploadHistoryTableWrapper from '../components/UploadHistoryTableWrapper'
import UploadImportStatusAlert from '../components/UploadImportStatusAlert'
import InstructionCard from '../components/common/InstructionCard'
import { inventoryUploadInstructions } from '../constants/uploadInstructions'
import { useUploadHistoryPolling } from '../hooks/useUploadHistoryPolling'

const InventoryUploadPage = () => {
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [uploadedInventory, setUploadedInventory] = useState([])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [processedItems, setProcessedItems] = useState(0)
  const [batchSize, setBatchSize] = useState(10)
  const [activeTab, setActiveTab] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)
  
  // Upload history states
  const [uploadLogs, setUploadLogs] = useState([])
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [errorDetailsDialog, setErrorDetailsDialog] = useState({ open: false, uploadLog: null })
  const [logsPage, setLogsPage] = useState(0)
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10)
  const [logsTotalCount, setLogsTotalCount] = useState(0)
  const logsFetchInFlightRef = useRef(false)

  const fetchUploadLogs = useCallback(async ({ silent = false } = {}) => {
    if (logsFetchInFlightRef.current) {
      return
    }
    logsFetchInFlightRef.current = true
    try {
      if (!silent) {
        setLoadingLogs(true)
      }
      const response = await inventoryService.getUploadLogs({
        page: logsPage + 1,
        limit: logsRowsPerPage,
        type: 'inventory'
      })
      
      // Handle response structure - API returns { items: [], pagination: {} }
      const logsData = response.items || response.data?.items || response.data || response.logs || response
      const totalCount = response.pagination?.total || response.data?.pagination?.total || response.totalCount || response.total || (Array.isArray(logsData) ? logsData.length : 0)
      
      setUploadLogs(Array.isArray(logsData) ? logsData : [])
      setLogsTotalCount(totalCount)
    } catch (error) {
      console.error('Error fetching upload logs:', error)
      if (!silent) {
        setUploadLogs([])
        setLogsTotalCount(0)
      }
    } finally {
      if (!silent) {
        setLoadingLogs(false)
      }
      logsFetchInFlightRef.current = false
    }
  }, [logsPage, logsRowsPerPage])

  // Fetch upload logs when tab or pagination changes
  useEffect(() => {
    if (activeTab === 1) {
      fetchUploadLogs()
    }
  }, [activeTab, logsPage, logsRowsPerPage, fetchUploadLogs])

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    if (newValue === 1) {
      fetchUploadLogs()
    }
  }

  const handleViewUploadHistory = useCallback(() => {
    setActiveTab(1)
    fetchUploadLogs()
  }, [fetchUploadLogs])

  useUploadHistoryPolling(activeTab === 1, fetchUploadLogs)

  // Handle showing error details
  const handleShowErrorDetails = async (logId) => {
    try {
      const response = await inventoryService.getUploadLogDetails(logId)
      setErrorDetailsDialog({ open: true, uploadLog: response.data || response })
    } catch (error) {
      console.error('Error fetching upload log details:', error)
    }
  }

  const handleLogsPageChange = (event, newPage) => {
    setLogsPage(newPage)
  }

  const handleLogsRowsPerPageChange = (event) => {
    setLogsRowsPerPage(parseInt(event.target.value, 10))
    setLogsPage(0)
  }



  const handleFileChange = (e) => {
    setFile(e.target.files[0])
  }

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    
    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const droppedFile = files[0]
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile)
      } else {
        setMessage({ type: 'error', text: 'Please upload a CSV file only' })
      }
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file' })
      return
    }

    try {
      setLoading(true)
      setUploadProgress(0)
      setProcessedItems(0)
      
      // Read file and parse CSV to count items
      const text = await file.text()
      const lines = text.split('\n').filter(line => line.trim())
      const itemCount = Math.max(0, lines.length - 1) // Subtract header
      setTotalItems(itemCount)
      
      const result = await inventoryService.importInventory({
        file,
        batchSize: batchSize.toString(),
      })
      
      // API now returns immediately - processing happens in background
      if (result.success && result.message) {
        setMessage({
          type: 'success',
          text: result.message || getUploadSuccessMessage('inventory'),
          showHistoryLink: true,
        })
        setUploadedInventory([]) // No immediate inventory since processing is async
        setFile(null)
        
        // Refresh upload logs to show the new upload
        fetchUploadLogs()
      } else {
        setMessage({ type: 'error', text: 'Failed to start inventory import' })
      }
      
      // Reset progress
      setUploadProgress(0)
      setProcessedItems(0)
      setTotalItems(0)
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error uploading inventory',
      })
      setUploadProgress(0)
      setProcessedItems(0)
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }

  // Download sample CSV
  const handleDownloadSampleCSV = () => {
    const sampleData = [
      ['article_code', 'quantity_on_hand', 'minimum_stock_level', 'maximum_stock_level', 'reorder_point', 'unit_cost', 'unit_price', 'location_code', 'notes', 'min_processing_time', 'min_processing_unit', 'max_processing_time', 'max_processing_unit'],
      ['SKU-001', '150', '20', '500', '100', '15.50', '25.99', 'warehouse_a', 'Stock checked', '1', 'day', '1', 'week'],
      ['SKU-002', '75', '10', '300', '50', '8.25', '14.99', 'warehouse_b', '', '12', 'hour', '24', 'hour'],
      ['SKU-003', '200', '30', '800', '150', '22.75', '39.99', 'store_front', 'High demand', '2', 'day', '3', 'day'],
      ['SKU-004', '45', '15', '200', '75', '12.00', '19.50', 'storage_room', 'Low stock alert', '1', 'week', '2', 'week'],
    ]

    const csv = sampleData
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'sample_inventory.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#1a1a1a' }}>
          Inventory Upload
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Update inventory levels and stock thresholds from CSV file
        </Typography>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Upload Inventory" icon={<CloudUploadIcon />} iconPosition="start" />
            <Tab label="Upload History" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Upload Tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Upload Section */}
            <Grid item xs={12} md={8}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  {message && (
                    <UploadImportStatusAlert
                      severity={message.type}
                      onClose={() => setMessage(null)}
                      showHistoryLink={message.showHistoryLink}
                      onViewUploadHistory={handleViewUploadHistory}
                    >
                      {message.text}
                    </UploadImportStatusAlert>
                  )}

                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon sx={{ color: '#e90000' }} />
                    Upload Inventory CSV
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        border: isDragOver ? '2px solid #1B5E20' : '2px dashed #e90000',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: isDragOver ? '#E8F5E9' : '#f8f9fb',
                        transition: 'all 0.3s ease',
                        '&:hover': { 
                          bgcolor: '#E8F5E9',
                          borderColor: '#1B5E20',
                        },
                      }}
                      onClick={() => document.getElementById('file-input').click()}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <CloudUploadIcon sx={{ fontSize: 56, color: '#e90000', mb: 2 }} />
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        {file ? file.name : isDragOver ? 'Drop CSV file here' : 'Click to upload or drag and drop'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        CSV file format (Max 10MB)
                      </Typography>
                      <input
                        id="file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        hidden
                      />
                    </Box>
                  </Box>

                  {/* Batch Size Configuration */}
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      label="Batch Size"
                      type="number"
                      value={batchSize}
                      onChange={(e) => setBatchSize(Math.max(1, parseInt(e.target.value, 10) || 10))}
                      fullWidth
                      size="small"
                      helperText="Number of inventory items to process per batch (default: 50)"
                      disabled={loading}
                    />
                  </Box>

                  {loading && uploadProgress > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                          Upload Progress
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                      {uploadProgress}% ({processedItems}/{totalItems} items)
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        backgroundColor: '#e90000',
                      },
                    }}
                  />
                </Box>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleUpload}
                disabled={loading || !file}
                sx={{
                  bgcolor: '#e90000',
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#1B5E20' },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                {loading ? 'Uploading...' : 'Upload Inventory'}
              </Button>

              {uploadedInventory.length > 0 && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  ✓ Successfully updated {uploadedInventory.length} inventory item(s)
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Help & Sample Section */}
        <Grid item xs={12} md={4}>
          {/* Sample Download Card */}
          <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <DownloadIcon sx={{ color: '#e90000', fontSize: 40 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Sample CSV
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                Download a sample CSV to see the required format.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<DownloadIcon sx={{ fontSize: 20 }} />}
                onClick={handleDownloadSampleCSV}
                sx={{
                  color: '#e90000',
                  borderColor: '#e90000',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#E8F5E9' },
                  py: 1.2,
                }}
              >
                Download Sample
              </Button>
            </CardContent>
          </Card>

          <InstructionCard {...inventoryUploadInstructions} icon={<InfoIcon />} />
        </Grid>

        {/* Uploaded Inventory Table - Full width in upload tab */}
        {uploadedInventory.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Updated Inventory ({uploadedInventory.length})
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Product ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Article Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Quantity
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Min Level
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Max Level
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadedInventory.slice(0, 20).map((item, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                          <TableCell>{item.product_id}</TableCell>
                          <TableCell sx={{ color: '#666' }}>{item.article_code || item.sku}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#e90000' }}>
                            {item.quantity_on_hand}
                          </TableCell>
                          <TableCell align="right">{item.minimum_stock_level}</TableCell>
                          <TableCell align="right">{item.maximum_stock_level}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {uploadedInventory.length > 20 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#999' }}>
                    Showing 20 of {uploadedInventory.length} inventory items
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    )}

    {/* History Tab */}
    {activeTab === 1 && (
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <HistoryIcon sx={{ mr: 1, color: '#e90000' }} />
              Upload History
            </Typography>
            <UploadHistoryRefreshButton
              onClick={fetchUploadLogs}
              loading={loadingLogs}
            />
          </Box>
          
          <UploadHistoryTableWrapper
            loading={loadingLogs}
            isEmpty={uploadLogs.length === 0}
            emptyState={
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <HistoryIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                  No Upload History
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  Your inventory uploads will appear here after you upload CSV files.
                </Typography>
              </Box>
            }
          >
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                      <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>File Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 100 }} align="center">Total</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 100 }} align="center">Success</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 100 }} align="center">Failed</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Upload Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 100 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadLogs.map((log) => (
                      <TableRow 
                        key={log.id} 
                        sx={{ 
                          '&:hover': { bgcolor: '#f8f9fb' },
                          '&:nth-of-type(even)': { bgcolor: '#fafafa' }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CloudUploadIcon sx={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {log.file_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <UploadLogStatusChip status={log.status} />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {log.total_rows}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                            {log.successful_imports}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {log.failed_imports > 0 ? (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#f44336' }}>
                              {log.failed_imports}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#999' }}>
                              0
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {formatDateTime(log.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {log.failed_imports > 0 ? (
                            <Tooltip title="View error details">
                              <IconButton
                                size="small"
                                onClick={() => handleShowErrorDetails(log.id)}
                                sx={{ 
                                  color: '#f44336',
                                  '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' }
                                }}
                              >
                                <ErrorIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Box sx={{ width: 32, height: 32 }} /> // Placeholder for alignment
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={logsTotalCount}
                page={logsPage}
                onPageChange={handleLogsPageChange}
                rowsPerPage={logsRowsPerPage}
                onRowsPerPageChange={handleLogsRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ borderTop: '1px solid #e0e0e0' }}
              />
            </>
          </UploadHistoryTableWrapper>
        </CardContent>
      </Card>
    )}

    {/* Error Details Dialog */}
    <UploadErrorDetailsDialog
      open={errorDetailsDialog.open}
      onClose={() => setErrorDetailsDialog({ open: false, uploadLog: null })}
      uploadLog={errorDetailsDialog.uploadLog}
    />
  </Container>
</>
)
}

export default InventoryUploadPage
